import { useCallback, useEffect, useMemo, useState } from "react";
import { activateUser, createUser, getUserFlatMappings, updateUser, getUsers } from "../../services/userService";
import Select from "../ui/Select";
import { getTowersBySociety } from "../../services/societyService";
import { getAssignedFlatIdsBySociety, getFlatsBySociety } from "../../services/flatsService";
import masterService from "../../services/masterEntitiesService";
import { toast } from "react-toastify";
import styles from "./UserForm.module.css";
import { buildUserSchema } from "../../schemas/userSchema";
import { zodErrorToFirstMessage } from "../../schemas/formSchemas";
import {
  getCachedAppSettings,
  getPrimaryRolesForPropertyType,
  getRoleAssignmentLevel,
  getSecondaryRolesFull,
} from "../../services/appSettingsService";
import { RefreshCw, Plus, X } from "lucide-react";
import { generateDeskCode, generateCabinCode } from "../../utils/codeGenerator";

const fieldClassName = styles.field;

const resolveAppRoleDefaults = (defaultRole, rolesList = [], activePropertyType = "Residential") => {
  const pType = String(activePropertyType || "").toLowerCase();
  const isComm = pType === "commercial";
  const isInd = pType === "industrial";

  if (!defaultRole) {
    if (isComm) return { primary_role: "Commercial Owner", secondary_role: "" };
    if (isInd) return { primary_role: "Industrial Director", secondary_role: "" };
    return { primary_role: "Owner", secondary_role: "" };
  }

  const needle = String(defaultRole).toLowerCase().trim();

  if (needle === "guard" || needle === "security guard" || needle === "security-guard") {
    return { primary_role: "Security Guard", secondary_role: "" };
  }

  if (needle === "gate receptionist" || needle === "gate-receptionist") {
    return { primary_role: "Gate Receptionist", secondary_role: "" };
  }

  if (
    needle === "receptionist" ||
    needle === "floor receptionist" ||
    needle === "floor-receptionist" ||
    needle === "industrial receptionist" ||
    needle === "industrial-receptionist"
  ) {
    return { primary_role: "Industrial Receptionist", secondary_role: "" };
  }

  if (
    needle === "industrial director" ||
    needle === "industrial-director" ||
    needle === "director"
  ) {
    return { primary_role: "Industrial Director", secondary_role: "" };
  }

  if (needle === "plant manager" || needle === "plant-manager") {
    return { primary_role: "Plant Manager", secondary_role: "" };
  }

  if (needle === "commercial owner" || needle === "commercial-owner") {
    return { primary_role: "Commercial Owner", secondary_role: "" };
  }

  if (needle === "commercial tenant" || needle === "commercial-tenant") {
    return { primary_role: "Commercial Tenant", secondary_role: "" };
  }

  if (needle === "office manager" || needle === "office-manager") {
    return { primary_role: "Office Manager", secondary_role: "" };
  }

  if (needle === "owner") {
    if (isComm) return { primary_role: "Commercial Owner", secondary_role: "" };
    return { primary_role: "Owner", secondary_role: "" };
  }

  if (needle === "tenant") {
    if (isComm) return { primary_role: "Commercial Tenant", secondary_role: "" };
    return { primary_role: "Tenant", secondary_role: "" };
  }

  const match = rolesList.find(
    (r) => !r.isSystem && (r.id?.toLowerCase() === needle || r.name?.toLowerCase() === needle)
  );

  if (match) {
    return { primary_role: match.name, secondary_role: "" };
  }

  return {
    primary_role: isComm ? "Commercial Owner" : isInd ? "Industrial Director" : "Owner",
    secondary_role: "",
  };
};

const AppUserForm = ({
  user,
  readOnly = false,
  onSuccess,
  formTitle = "App User Details",
  showGenderField = true,
  showOsTypeField = true,
  showFlatAssignments: propsShowFlatAssignments = true,
  showFamilyTab = true,
  showVehiclesTab = true,
  societies = [],
  defaultSocietyId = "",
  lockSociety = false,
  defaultRole = "owner",
  propertyType = "",
}) => {
  const [activeTab, setActiveTab] = useState("personal");
  const [form, setForm] = useState({
    full_name: "",
    email_id: "",
    mobile_number: "",
    gender: "Male",
    primary_role: "Owner",
    secondary_role: "",
    os_type: "Android",
    society_id: defaultSocietyId ? String(defaultSocietyId) : "",
    role_details: { is_member: "no", role: "" },
  });

  const [error, setError] = useState("");
  const [towerLoadError, setTowerLoadError] = useState("");
  const [towers, setTowers] = useState([]);
  const [flats, setFlats] = useState([]);
  const [societyUsers, setSocietyUsers] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [assignedFlatIds, setAssignedFlatIds] = useState(() => new Set());

  const [flatMappings, setFlatMappings] = useState([
    { tower_id: "", flat_id: "", ownership_type: "Owner" },
  ]);
  const [towerMappings, setTowerMappings] = useState([{ tower_id: "" }]);
  const [receptionistTowerId, setReceptionistTowerId] = useState("");
  const [receptionistFloor, setReceptionistFloor] = useState("");
  const [receptionistDeskFlatId, setReceptionistDeskFlatId] = useState("");
  const [familyMembers, setFamilyMembers] = useState([
    { member_name: "", relation: "", member_type: "Adult" },
  ]);
  const [vehicles, setVehicles] = useState([
    { vehicle_number: "", vehicle_type: "Car", model: "", color: "", parking_slot: "" },
  ]);

  const [linkType, setLinkType] = useState(() => {
    if (user?.role_details?.linked_owner_id) return "owner";
    if (user?.role_details?.linked_business_name || user?.role_details?.company_name) return "business";
    return "none";
  });
  const [selectedOwnerId, setSelectedOwnerId] = useState(
    user?.role_details?.linked_owner_id ? String(user.role_details.linked_owner_id) : ""
  );
  const [selectedBusinessName, setSelectedBusinessName] = useState(
    user?.role_details?.linked_business_name || user?.role_details?.company_name || ""
  );

  const [deactivatedUser, setDeactivatedUser] = useState(null);
  const [activating, setActivating] = useState(false);

  const selectedSocietyObj = useMemo(() => {
    return societies.find((s) => String(s.society_id) === String(form.society_id));
  }, [societies, form.society_id]);

  const activePropertyType = useMemo(() => {
    if (selectedSocietyObj?.society_type) return selectedSocietyObj.society_type;
    if (propertyType) return propertyType;
    if (user?.society_id) {
      const uSoc = societies.find((s) => String(s.society_id) === String(user.society_id));
      if (uSoc?.society_type) return uSoc.society_type;
    }
    return "Residential";
  }, [propertyType, selectedSocietyObj, user, societies]);

  const isResidential = activePropertyType.toLowerCase() === "residential";
  const isCommercial = activePropertyType.toLowerCase() === "commercial";
  const isIndustrial = activePropertyType.toLowerCase() === "industrial";
  const isHybrid = activePropertyType.toLowerCase().includes("hybrid") || activePropertyType.toLowerCase() === "mixed";

  // App roles filtered strictly according to the active property type
  const primaryRoles = useMemo(() => {
    let roles = getPrimaryRolesForPropertyType(activePropertyType);
    if (isCommercial) {
      roles = roles.filter(
        (r) => !["Resident", "Owner", "Tenant", "Industrial Director", "Plant Manager", "Gate Receptionist", "Floor Receptionist", "Director's Receptionist"].includes(r)
      );
    } else if (isIndustrial) {
      roles = roles.filter(
        (r) => !["Resident", "Owner", "Tenant", "Commercial Owner", "Commercial Tenant", "Office Manager"].includes(r)
      );
    } else if (isResidential) {
      roles = roles.filter(
        (r) => !["Commercial Owner", "Commercial Tenant", "Office Manager", "Industrial Director", "Director", "Plant Manager", "Gate Receptionist", "Floor Receptionist", "Director's Receptionist", "Department Head", "Head of Department (HOD)", "Team Leader", "Team Lead (TL)", "Corporate Employee", "Employee"].includes(r)
      );
    } else if (isHybrid) {
      roles = roles.filter(
        (r) => !["Industrial Director", "Director", "Plant Manager", "Gate Receptionist", "Floor Receptionist", "Director's Receptionist"].includes(r)
      );
    }
    return Array.from(new Set(roles));
  }, [activePropertyType, isCommercial, isIndustrial, isResidential, isHybrid]);

  const currentRole = form.primary_role || "";
  const isGuard =
    currentRole.toLowerCase().includes("guard") ||
    String(form.secondary_role || "").toLowerCase().includes("guard");
  const isReceptionist =
    currentRole.toLowerCase().includes("receptionist") ||
    String(form.secondary_role || "").toLowerCase().includes("receptionist");
  const isGateReceptionist = currentRole.toLowerCase().includes("gate");
  const isFloorReceptionist = isReceptionist && !isGateReceptionist;
  const isStaffRole = isGuard || isReceptionist || currentRole.toLowerCase() === "staff";

  const isTenant =
    currentRole === "Tenant" ||
    currentRole === "Commercial Tenant" ||
    String(form.secondary_role || "").toLowerCase() === "tenant";

  const isCommercialRole =
    currentRole === "Commercial Owner" ||
    currentRole === "Commercial Tenant" ||
    currentRole === "Office Manager" ||
    (isCommercial && !isStaffRole);

  const isIndustrialRole =
    currentRole === "Industrial Director" ||
    currentRole === "Plant Manager" ||
    (isIndustrial && !isStaffRole);

  const isResidentialRole =
    currentRole === "Owner" ||
    currentRole === "Tenant" ||
    currentRole === "Resident" ||
    (isResidential && !isStaffRole);

  const defaultIndustrialDepartments = useMemo(
    () => [
      "Executive Board",
      "Operations",
      "Finance & Accounts",
      "HR & Personnel",
      "Administration",
      "IT & Engineering",
      "Sales & Business Development",
      "Marketing & Creative",
      "Purchase & Procurement",
      "Legal & Liaison",
      "Civil & Projects",
      "Warehouse & Logistics",
      "Quality Assurance (QA/QC)",
      "Health, Safety & Environment (HSE)",
      "Manufacturing & Production",
      "Housekeeping & Security",
      "Food & Beverage (Pantry)",
    ],
    []
  );

  const departmentOptions = useMemo(() => {
    const currentDept = form.role_details?.department;
    const merged = new Set([
      ...availableDepartments,
      ...defaultIndustrialDepartments,
      ...(currentDept ? [currentDept] : []),
    ]);
    return Array.from(merged);
  }, [availableDepartments, defaultIndustrialDepartments, form.role_details?.department]);

  const unitTerm = isIndustrialRole ? "Unit / Shed" : isCommercialRole ? "Office / Unit" : "Flat";
  const occupancyOptions = isIndustrialRole
    ? [
        { label: "Owner / Director", value: "Owner" },
        { label: "Lessee / Tenant", value: "Tenant" },
        { label: "Operator / Contractor", value: "Operator" },
      ]
    : isCommercialRole
    ? [
        { label: "Owner", value: "Owner" },
        { label: "Tenant", value: "Tenant" },
        { label: "Leaseholder", value: "Leaseholder" },
      ]
    : [
        { label: "Owner", value: "Owner" },
        { label: "Tenant", value: "Tenant" },
      ];

  const allSecondaryRoles = getSecondaryRolesFull();
  let secondaryRoles = [];
  if (String(form.primary_role).toLowerCase() === "resident") {
    let baseRoles = ["Owner", "Tenant"];
    if (isIndustrial) {
      baseRoles = ["Industrial Director", "Plant Manager", "Owner", "Tenant"];
    } else if (isCommercial) {
      baseRoles = ["Commercial Owner", "Commercial Tenant", "Office Manager"];
    }
    const fromSettings = allSecondaryRoles
      .filter((r) => {
        const isScopeMatch =
          String(r.scope).toUpperCase() === "RESIDENT" ||
          String(r.scope).toUpperCase() === "CORPORATE" ||
          String(r.scope).toUpperCase() === "MANAGEMENT";
        if (!isScopeMatch) return false;
        if (!Array.isArray(r.propertyTypes) || !r.propertyTypes.length) return true;
        return r.propertyTypes.some((pt) => pt.toLowerCase() === activePropertyType.toLowerCase());
      })
      .map((r) => r.name);
    secondaryRoles = Array.from(new Set([...baseRoles, ...fromSettings]));
  } else if (String(form.primary_role).toLowerCase() === "staff") {
    let baseRoles = ["Security Guard", "Staff"];
    if (isIndustrial) {
      baseRoles = ["Gate Receptionist", "Receptionist", "Industrial Receptionist", "Security Guard", "Staff"];
    } else if (isCommercial) {
      baseRoles = ["Receptionist", "Security Guard", "Staff"];
    }
    const fromSettings = allSecondaryRoles
      .filter((r) => {
        const isScopeMatch = String(r.scope).toUpperCase() === "OPERATIONS";
        if (!isScopeMatch) return false;
        if (!Array.isArray(r.propertyTypes) || !r.propertyTypes.length) return true;
        return r.propertyTypes.some((pt) => pt.toLowerCase() === activePropertyType.toLowerCase());
      })
      .map((r) => r.name);
    secondaryRoles = Array.from(new Set([...baseRoles, ...fromSettings]));
  } else {
    secondaryRoles = allSecondaryRoles.map((r) => r.name);
  }

  const appSettings = getCachedAppSettings();
  const currentRoleForAssignment = form.secondary_role || form.primary_role;
  const assignmentLevel = getRoleAssignmentLevel(appSettings, currentRoleForAssignment);

  const showFlatAssignments =
    propsShowFlatAssignments &&
    !isFloorReceptionist &&
    (isCommercialRole || isIndustrialRole || isResidentialRole || assignmentLevel === "Flat" || assignmentLevel === "Floor");
  const showTowerAssignments =
    propsShowFlatAssignments &&
    !isFloorReceptionist &&
    assignmentLevel === "Tower";
  const actualShowFamilyTab = showFamilyTab && isResidentialRole;
  const actualShowVehiclesTab = showVehiclesTab && !isStaffRole;

  const availableTabs = useMemo(() => {
    const tabs = ["personal"];
    if (actualShowFamilyTab) tabs.push("family");
    if (actualShowVehiclesTab) tabs.push("vehicles");
    if (isGuard) tabs.push("guardDetails");
    return tabs;
  }, [actualShowFamilyTab, actualShowVehiclesTab, isGuard]);

  // Existing Owners in the selected property
  const existingOwners = useMemo(() => {
    if (!Array.isArray(societyUsers)) return [];
    return societyUsers
      .filter((u) => {
        const p = String(u.primary_role || "").toLowerCase();
        const s = String(u.secondary_role || "").toLowerCase();
        const isOwnerRole = p.includes("owner") || s.includes("owner");
        const hasOwnerFlat = Array.isArray(u.user_flat_mapping) &&
          u.user_flat_mapping.some((m) => m.ownership_type === "Owner");
        return isOwnerRole || hasOwnerFlat;
      })
      .map((u) => {
        const ownerFlats = [];
        if (Array.isArray(u.user_flat_mapping)) {
          u.user_flat_mapping.forEach((m) => {
            const matched = flats.find((f) => Number(f.flat_id) === Number(m.flat_id));
            if (matched) {
              const tw = towers.find((t) => Number(t.tower_id) === Number(matched.floors?.tower_id || matched.tower_id));
              ownerFlats.push({
                ...matched,
                tower_name: tw?.tower_name || "",
                tower_id: tw?.tower_id || matched.floors?.tower_id || matched.tower_id,
              });
            }
          });
        }
        return {
          userId: u.user_id,
          name: u.full_name,
          phone: u.mobile_number,
          company: u.role_details?.company_name || "",
          flats: ownerFlats,
        };
      });
  }, [societyUsers, flats, towers]);

  // Existing Businesses / Companies in the selected property
  const existingBusinesses = useMemo(() => {
    if (!Array.isArray(societyUsers)) return [];
    const map = new Map();
    societyUsers.forEach((u) => {
      const comp = u.role_details?.company_name || u.role_details?.business_name;
      if (!comp || !String(comp).trim()) return;
      const cleanName = String(comp).trim();
      const existing = map.get(cleanName) || {
        name: cleanName,
        contactPerson: u.full_name,
        phone: u.role_details?.office_phone || u.mobile_number,
        gst: u.role_details?.gst_number || "",
        users: [],
        flatIds: new Set(),
      };
      existing.users.push(u);
      if (Array.isArray(u.user_flat_mapping)) {
        u.user_flat_mapping.forEach((m) => existing.flatIds.add(Number(m.flat_id)));
      }
      map.set(cleanName, existing);
    });

    return Array.from(map.values()).map((b) => {
      const bFlats = Array.from(b.flatIds)
        .map((fid) => {
          const matched = flats.find((f) => Number(f.flat_id) === fid);
          if (!matched) return null;
          const tw = towers.find((t) => Number(t.tower_id) === Number(matched.floors?.tower_id || matched.tower_id));
          return {
            ...matched,
            tower_name: tw?.tower_name || "",
            tower_id: tw?.tower_id || matched.floors?.tower_id || matched.tower_id,
          };
        })
        .filter(Boolean);

      return {
        ...b,
        flats: bFlats,
      };
    });
  }, [societyUsers, flats, towers]);


  const handleSelectOwner = (ownerId) => {
    setSelectedOwnerId(ownerId);
    if (!ownerId) {
      setForm((prev) => ({
        ...prev,
        role_details: {
          ...(prev.role_details || {}),
          linked_owner_id: null,
          linked_owner_name: "",
          link_type: linkType,
        },
      }));
      return;
    }

    const owner = existingOwners.find((o) => String(o.userId) === String(ownerId));
    if (owner && owner.flats.length > 0) {
      const newMappings = owner.flats.map((f) => ({
        tower_id: f.tower_id ? String(f.tower_id) : "",
        flat_id: String(f.flat_id),
        ownership_type: "Tenant",
      }));
      setFlatMappings(newMappings);
      toast.info(`Assigned ${unitTerm} ${owner.flats.map((f) => f.flat_number).join(", ")} owned by ${owner.name}`);
    }

    setForm((prev) => ({
      ...prev,
      role_details: {
        ...(prev.role_details || {}),
        linked_owner_id: owner?.userId || null,
        linked_owner_name: owner?.name || "",
        link_type: "owner",
      },
    }));
  };

  const assignBusinessUnits = (biz) => {
    if (!biz || !biz.flats.length) return;
    const isTenantOcc = isTenant;
    const newMappings = biz.flats.map((f) => ({
      tower_id: f.tower_id ? String(f.tower_id) : "",
      flat_id: String(f.flat_id),
      ownership_type: isTenantOcc ? "Tenant" : "Owner",
    }));
    setFlatMappings(newMappings);
    toast.success(`Assigned ${biz.flats.length} unit(s) of ${biz.name}`);
  };

  const handleSelectBusiness = (bName) => {
    setSelectedBusinessName(bName);
    if (!bName) {
      setForm((prev) => ({
        ...prev,
        role_details: {
          ...(prev.role_details || {}),
          company_name: "",
          linked_business_name: "",
          link_type: linkType,
        },
      }));
      return;
    }

    const biz = existingBusinesses.find((b) => b.name.toLowerCase() === bName.toLowerCase());
    if (biz) {
      assignBusinessUnits(biz);
      setForm((prev) => ({
        ...prev,
        role_details: {
          ...(prev.role_details || {}),
          company_name: biz.name,
          linked_business_name: biz.name,
          gst_number: biz.gst || prev.role_details?.gst_number || "",
          office_phone: biz.phone || prev.role_details?.office_phone || "",
          link_type: "business",
        },
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        role_details: {
          ...(prev.role_details || {}),
          company_name: bName,
          linked_business_name: bName,
          link_type: "business",
        },
      }));
    }
  };

  const handleRegenerateDeskCode = () => {
    const dept = form.role_details?.department || "";
    const existingDeskCodes = (societyUsers || [])
      .map((u) => u.role_details?.desk_code)
      .filter(Boolean)
      .concat(form.role_details?.desk_code ? [form.role_details.desk_code] : []);
    const newCode = generateDeskCode(dept, existingDeskCodes);
    setForm((prev) => ({
      ...prev,
      role_details: {
        ...(prev.role_details || {}),
        desk_code: newCode,
      },
    }));
    toast.info(`Regenerated Desk Code: ${newCode}`);
  };

  const handleRegenerateCabinCode = () => {
    const dept = form.role_details?.department || "";
    const existingCabinCodes = (societyUsers || [])
      .map((u) => u.role_details?.cabin_code)
      .filter(Boolean)
      .concat(form.role_details?.cabin_code ? [form.role_details.cabin_code] : []);
    const newCode = generateCabinCode(dept, existingCabinCodes);
    setForm((prev) => ({
      ...prev,
      role_details: {
        ...(prev.role_details || {}),
        cabin_code: newCode,
      },
    }));
    toast.info(`Regenerated Cabin Code: ${newCode}`);
  };

  const handleClearCabinCode = () => {
    setForm((prev) => ({
      ...prev,
      role_details: {
        ...(prev.role_details || {}),
        cabin_code: "",
      },
    }));
  };

  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab("personal");
    }
  }, [activeTab, availableTabs]);

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || "",
        email_id: user.email_id || "",
        mobile_number: user.mobile_number || "",
        gender: user.gender || "Male",
        primary_role: user.primary_role || "Owner",
        secondary_role: user.secondary_role || "",
        os_type: user.os_type || "Android",
        society_id: user.society_id ? String(user.society_id) : (defaultSocietyId ? String(defaultSocietyId) : ""),
        role_details: user.role_details || { is_member: "no", role: "" },
      });
      setFamilyMembers(
        Array.isArray(user.family_members) && user.family_members.length
          ? user.family_members
          : [{ member_name: "", relation: "", member_type: "Adult" }]
      );
      setVehicles(
        Array.isArray(user.vehicles) && user.vehicles.length
          ? user.vehicles
          : [{ vehicle_number: "", vehicle_type: "Car", model: "", color: "", parking_slot: "" }]
      );
      if (user.role_details) {
        const rd = user.role_details;
        if (rd.assigned_floor != null || rd.floor_number != null) {
          setReceptionistFloor(String(rd.assigned_floor ?? rd.floor_number));
        }
        if (rd.tower_id != null) {
          setReceptionistTowerId(String(rd.tower_id));
        }
        if (rd.linked_owner_id) {
          setSelectedOwnerId(String(rd.linked_owner_id));
          setLinkType("owner");
        } else if (rd.linked_business_name || rd.company_name) {
          setSelectedBusinessName(rd.linked_business_name || rd.company_name);
          setLinkType("business");
        }
      }
      if (Array.isArray(user.tower_mappings) && user.tower_mappings.length) {
        setReceptionistTowerId(String(user.tower_mappings[0].tower_id));
      }
      if (Array.isArray(user.flat_mappings) && user.flat_mappings.length) {
        setReceptionistDeskFlatId(String(user.flat_mappings[0].flat_id));
      }

      setActiveTab("personal");
      setError("");
      setDeactivatedUser(null);
      return;
    }

    const resolved = resolveAppRoleDefaults(defaultRole, appSettings?.roles || [], activePropertyType);
    const initialOwnership = resolved.primary_role.toLowerCase().includes("tenant") ? "Tenant" : "Owner";

    setForm({
      full_name: "",
      email_id: "",
      mobile_number: "",
      gender: "Male",
      primary_role: resolved.primary_role,
      secondary_role: resolved.secondary_role,
      os_type: "Android",
      society_id: defaultSocietyId ? String(defaultSocietyId) : "",
      role_details: { is_member: "no", role: "" },
    });
    setFlatMappings([{ tower_id: "", flat_id: "", ownership_type: initialOwnership }]);
    setReceptionistTowerId("");
    setReceptionistFloor("");
    setReceptionistDeskFlatId("");
    setFamilyMembers([{ member_name: "", relation: "", member_type: "Adult" }]);
    setVehicles([{ vehicle_number: "", vehicle_type: "Car", model: "", color: "", parking_slot: "" }]);
    setActiveTab("personal");
    setError("");
    setDeactivatedUser(null);
  }, [defaultRole, defaultSocietyId, user, activePropertyType]);

  // Ensure primary_role stays in sync with allowable primaryRoles for current property type
  useEffect(() => {
    if (primaryRoles.length > 0 && !primaryRoles.includes(form.primary_role)) {
      setForm((prev) => ({
        ...prev,
        primary_role: primaryRoles[0],
      }));
    }
  }, [primaryRoles, form.primary_role]);

  const loadSocietyData = useCallback(async (societyId) => {
    if (!societyId) {
      setTowers([]);
      setFlats([]);
      setSocietyUsers([]);
      setAvailableDepartments([]);
      setTowerLoadError("");
      return;
    }

    try {
      const [towerResult, flatResult, usersResult, deptResult, masterDeptResult] = await Promise.allSettled([
        getTowersBySociety(societyId),
        getFlatsBySociety(societyId),
        getUsers(societyId),
        masterService.getDepartments(societyId),
        masterService.getMasterDepartmentTypes({ property_type_code: "IND" }),
      ]);

      const towerRows = towerResult.status === "fulfilled" ? towerResult.value : [];
      const flatRows = flatResult.status === "fulfilled" ? flatResult.value : [];
      const userRows = usersResult.status === "fulfilled" ? usersResult.value : [];
      const depts1 = deptResult.status === "fulfilled" && Array.isArray(deptResult.value) ? deptResult.value.map((d) => d.name) : [];
      const depts2 = masterDeptResult.status === "fulfilled" && Array.isArray(masterDeptResult.value) ? masterDeptResult.value.map((d) => d.name) : [];

      setTowers(Array.isArray(towerRows) ? towerRows : []);
      setFlats(Array.isArray(flatRows) ? flatRows : []);
      setSocietyUsers(Array.isArray(userRows) ? userRows : []);
      setAvailableDepartments(Array.from(new Set([...depts1, ...depts2].filter(Boolean))));

      if (towerResult.status === "rejected") {
        const reason = towerResult.reason;
        const msg = reason?.data?.error || reason?.data?.message || reason?.message || "Unable to load towers.";
        setTowerLoadError(msg);
      } else {
        setTowerLoadError("");
      }
    } catch (err) {
      console.error(err);
      setTowers([]);
      setFlats([]);
      setSocietyUsers([]);
      setAvailableDepartments([]);
      setTowerLoadError("Unable to load property data.");
    }
  }, []);

  useEffect(() => {
    if (!form.society_id) return;
    loadSocietyData(form.society_id);
  }, [form.society_id, loadSocietyData]);

  useEffect(() => {
    const loadAssigned = async () => {
      if (!showFlatAssignments || !form.society_id) {
        setAssignedFlatIds(new Set());
        return;
      }

      try {
        const ids = await getAssignedFlatIdsBySociety(form.society_id);
        setAssignedFlatIds(new Set((ids || []).map((n) => Number(n))));
      } catch (err) {
        console.error(err);
        setAssignedFlatIds(new Set());
      }
    };

    loadAssigned();
  }, [form.society_id, showFlatAssignments]);

  useEffect(() => {
    const loadMappings = async () => {
      if (!showFlatAssignments) {
        setFlatMappings([{ tower_id: "", flat_id: "", ownership_type: "Owner" }]);
        return;
      }
      if (!user?.user_id) return;

      if (readOnly && Array.isArray(user?.flat_mappings) && user.flat_mappings.length) {
        const rows = user.flat_mappings
          .map((m) => {
            const flatId = m?.flat_id ? Number(m.flat_id) : "";
            const matchedFlat = flats.find((f) => Number(f.flat_id) === flatId);
            return {
              tower_id: matchedFlat?.tower_id ? String(matchedFlat.tower_id) : "",
              flat_id: flatId ? String(flatId) : "",
              ownership_type: m?.ownership_type === "Tenant" ? "Tenant" : "Owner",
            };
          })
          .filter((m) => m.flat_id);

        setFlatMappings(rows.length ? rows : [{ tower_id: "", flat_id: "", ownership_type: "Owner" }]);
        return;
      }

      try {
        const res = await getUserFlatMappings(user.user_id);
        const rows = (Array.isArray(res?.data) ? res.data : [])
          .map((m) => {
            const flatId = m?.flat_id ? Number(m.flat_id) : "";
            const matchedFlat = flats.find((f) => Number(f.flat_id) === flatId);
            return {
              tower_id: matchedFlat?.tower_id ? String(matchedFlat.tower_id) : "",
              flat_id: flatId ? String(flatId) : "",
              ownership_type: m.ownership_type === "Tenant" ? "Tenant" : "Owner",
            };
          })
          .filter((m) => m.flat_id);

        if (rows.length) {
          setFlatMappings(rows);
        } else {
          setFlatMappings([{ tower_id: "", flat_id: "", ownership_type: "Owner" }]);
        }
      } catch (e) {
        console.error(e);
      }
    };

    loadMappings();
  }, [readOnly, user?.user_id, user?.flat_mappings, flats, showFlatAssignments]);

  const handleChange = (e) => {
    if (readOnly) return;
    setError("");
    if (deactivatedUser) setDeactivatedUser(null);

    const { name, value } = e.target;

    if (name === "mobile_number") {
      const sanitized = value.replace(/\D/g, "").slice(0, 10);
      setForm((prev) => ({ ...prev, [name]: sanitized }));
      return;
    }

    if (name.startsWith("role_details.")) {
      const field = name.split(".")[1];
      setForm((prev) => ({
        ...prev,
        role_details: { ...prev.role_details, [field]: value },
      }));
      return;
    }

    if (name === "primary_role") {
      let nextSecondaryRole = "";
      if (value === "Resident") {
        nextSecondaryRole = form.secondary_role || "Owner";
      } else if (value === "Staff") {
        nextSecondaryRole = form.secondary_role || "Security Guard";
      }
      setForm((prev) => ({
        ...prev,
        primary_role: value,
        secondary_role: nextSecondaryRole,
      }));
      return;
    }

    if (name === "secondary_role") {
      setForm((prev) => ({ ...prev, secondary_role: value }));
      if (value === "Tenant" && flatMappings.length === 1 && !flatMappings[0].flat_id) {
        setFlatMappings([{ tower_id: "", flat_id: "", ownership_type: "Tenant" }]);
      } else if (value === "Owner" && flatMappings.length === 1 && !flatMappings[0].flat_id) {
        setFlatMappings([{ tower_id: "", flat_id: "", ownership_type: "Owner" }]);
      }
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSocietyChange = (e) => {
    if (readOnly || lockSociety) return;
    const value = e.target.value;
    setForm((prev) => ({ ...prev, society_id: value }));
    setFlatMappings([{ tower_id: "", flat_id: "", ownership_type: "Owner" }]);
  };

  const handleFlatMappingChange = (index, field, value) => {
    if (readOnly) return;
    setFlatMappings((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === "tower_id") {
        next[index].flat_id = "";
      }
      return next;
    });
  };

  const addFlatRow = () => {
    if (readOnly) return;
    setFlatMappings((prev) => [
      ...prev,
      { tower_id: "", flat_id: "", ownership_type: form.secondary_role === "Tenant" ? "Tenant" : "Owner" },
    ]);
  };

  const removeFlatRow = (index) => {
    if (readOnly) return;
    setFlatMappings((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleTowerMappingChange = (index, field, value) => {
    if (readOnly) return;
    setTowerMappings((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addTowerRow = () => {
    if (readOnly) return;
    setTowerMappings((prev) => [...prev, { tower_id: "" }]);
  };

  const removeTowerRow = (index) => {
    if (readOnly) return;
    setTowerMappings((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateFamilyMember = (index, field, value) => {
    if (readOnly) return;
    setFamilyMembers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addFamilyMember = () => {
    if (readOnly) return;
    setFamilyMembers((prev) => [...prev, { member_name: "", relation: "", member_type: "Adult" }]);
  };

  const removeFamilyMember = (index) => {
    if (readOnly) return;
    setFamilyMembers((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateVehicle = (index, field, value) => {
    if (readOnly) return;
    setVehicles((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addVehicle = () => {
    if (readOnly) return;
    setVehicles((prev) => [
      ...prev,
      { vehicle_number: "", vehicle_type: "Car", model: "", color: "", parking_slot: "" },
    ]);
  };

  const removeVehicle = (index) => {
    if (readOnly) return;
    setVehicles((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const flatOptionsByTower = useMemo(() => {
    const allowedForThisUser = new Set(
      flatMappings
        .map((m) => Number(m.flat_id))
        .filter((n) => Number.isInteger(n) && n > 0)
    );

    const mergedFlatIds = new Set(
      flats
        .filter((flat) => String(flat?.flat_number || "").includes("+"))
        .map((flat) => Number(flat.flat_id))
        .filter((n) => Number.isInteger(n) && n > 0)
    );

    const byTower = new Map();
    for (const flat of flats) {
      const flatId = Number(flat?.flat_id);
      const isAssigned = assignedFlatIds.has(flatId);
      const canUse = !isAssigned || allowedForThisUser.has(flatId);
      if (!canUse) continue;

      const flatNumber = String(flat?.flat_number || "");
      const unitType = String(flat?.unit_type || "");

      if (flatNumber.includes("+")) {
        // merged
      } else if (flat.merged_from && mergedFlatIds.size) {
        if (!allowedForThisUser.has(flatId)) continue;
      }

      if (unitType.toUpperCase() === "MERGED" || flatNumber.startsWith("M-")) continue;

      const key = String(flat.tower_id || flat.floors?.tower_id || "");
      if (!byTower.has(key)) byTower.set(key, []);
      byTower.get(key).push(flat);
    }

    for (const list of byTower.values()) {
      list.sort((a, b) => {
        const floorA = a.floors?.floor_number ?? 0;
        const floorB = b.floors?.floor_number ?? 0;
        if (floorA !== floorB) return floorA - floorB;
        return String(a.flat_number).localeCompare(String(b.flat_number), undefined, { numeric: true });
      });
    }

    return byTower;
  }, [assignedFlatIds, flatMappings, flats]);

  const availableFloorsForTower = useMemo(() => {
    if (!receptionistTowerId) return [];
    const towerFlats = flatOptionsByTower.get(String(receptionistTowerId)) || [];
    const floorNums = new Set();
    for (const f of towerFlats) {
      if (f.floors?.floor_number != null) {
        floorNums.add(f.floors.floor_number);
      }
    }
    return Array.from(floorNums).sort((a, b) => a - b);
  }, [receptionistTowerId, flatOptionsByTower]);

  const availableDeskFlats = useMemo(() => {
    if (!receptionistTowerId || !receptionistFloor) return [];
    const towerFlats = flatOptionsByTower.get(String(receptionistTowerId)) || [];
    return towerFlats.filter(
      (f) => String(f.floors?.floor_number) === String(receptionistFloor)
    );
  }, [receptionistTowerId, receptionistFloor, flatOptionsByTower]);

  const handleActivateDeactivatedUser = async () => {
    if (!deactivatedUser?.userId) return;
    setActivating(true);
    try {
      await activateUser(deactivatedUser.userId, {
        full_name: form.full_name || undefined,
        primary_role: form.primary_role,
        secondary_role: form.secondary_role,
        system_role: "",
        society_id: form.society_id ? Number(form.society_id) : null,
        ...(showFlatAssignments ? { flat_mappings: flatMappings.filter((m) => m.flat_id) } : {}),
      });

      toast.success("User activated successfully!");
      setDeactivatedUser(null);
      if (onSuccess) onSuccess();
    } catch (actErr) {
      console.error(actErr);
      const msg = actErr?.data?.error || actErr?.data?.message || actErr?.message || "Failed to activate user.";
      toast.error(msg);
    } finally {
      setActivating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setError("");

    const schema = buildUserSchema({ showPasswordField: false });
    const parsed = schema.safeParse({
      full_name: form.full_name,
      email_id: form.email_id,
      mobile_number: form.mobile_number,
      gender: form.gender,
      primary_role: form.primary_role,
      secondary_role: form.secondary_role,
      system_role: "",
      has_portal_access: false,
      os_type: form.os_type,
      society_id: form.society_id,
      role_details: form.role_details,
    });

    if (!parsed.success) {
      setError(zodErrorToFirstMessage(parsed.error));
      return;
    }

    try {
      const expandFlatIds = (flatId) => {
        const id = Number(flatId);
        if (!Number.isInteger(id) || id <= 0) return [];
        const selected = flats.find((f) => Number(f.flat_id) === id);
        if (!selected) return [id];
        const flatNumber = String(selected.flat_number || "");
        if (!flatNumber.includes("+")) return [id];

        const members = flats
          .filter((f) => String(f.merged_from || "") === flatNumber)
          .filter((f) => !String(f.flat_number || "").includes("+"))
          .map((f) => Number(f.flat_id))
          .filter((n) => Number.isInteger(n) && n > 0);

        return Array.from(new Set([id, ...members]));
      };

      const linkedOwner = linkType === "owner" && selectedOwnerId
        ? existingOwners.find((o) => String(o.id) === String(selectedOwnerId))
        : null;

      const payload = {
        ...parsed.data,
        account_type: "app",
        has_portal_access: false,
        system_role: "",
        secondary_role: form.secondary_role,
        society_id: form.society_id ? Number(form.society_id) : null,
        role_details: {
          ...(form.role_details || {}),
          link_type: linkType,
          linked_owner_id: linkedOwner ? Number(linkedOwner.id) : (form.role_details?.linked_owner_id || null),
          linked_owner_name: linkedOwner ? linkedOwner.name : (form.role_details?.linked_owner_name || null),
          linked_business_name:
            linkType === "business"
              ? (selectedBusinessName || form.role_details?.company_name || null)
              : (form.role_details?.company_name || null),
          ...(isFloorReceptionist
            ? {
                assigned_floor: receptionistFloor ? Number(receptionistFloor) : null,
                floor_number: receptionistFloor ? Number(receptionistFloor) : null,
                tower_id: receptionistTowerId ? Number(receptionistTowerId) : null,
              }
            : {}),
        },
        ...(!isFloorReceptionist && showFlatAssignments
          ? {
              flat_mappings: (() => {
                const expanded = flatMappings
                  .flatMap((m) => {
                    const ownership_type = m.ownership_type === "Tenant" ? "Tenant" : "Owner";
                    return expandFlatIds(m.flat_id).map((flat_id) => ({
                      flat_id,
                      ownership_type,
                    }));
                  })
                  .filter((m) => Number.isInteger(m.flat_id) && m.flat_id > 0);

                const unique = new Map();
                for (const mapping of expanded) {
                  if (!unique.has(mapping.flat_id)) unique.set(mapping.flat_id, mapping);
                }
                return Array.from(unique.values());
              })(),
            }
          : {}),
        ...(!isFloorReceptionist && showTowerAssignments
          ? {
              tower_mappings: towerMappings
                .filter((m) => m.tower_id)
                .map((m) => ({ tower_id: Number(m.tower_id) })),
            }
          : {}),
        ...(actualShowFamilyTab
          ? {
              family_members: familyMembers
                .map((m) => ({
                  member_name: String(m?.member_name ?? "").trim(),
                  relation: String(m?.relation ?? "").trim(),
                  member_type: m?.member_type || "Other",
                }))
                .filter((m) => m.member_name),
            }
          : {}),
        ...(actualShowVehiclesTab
          ? {
              vehicles: vehicles
                .map((v) => ({
                  vehicle_number: String(v?.vehicle_number ?? "").trim(),
                  vehicle_type: v?.vehicle_type || "Other",
                  model: String(v?.model ?? "").trim(),
                  color: String(v?.color ?? "").trim(),
                  parking_slot: String(v?.parking_slot ?? "").trim(),
                }))
                .filter((v) => v.vehicle_number),
            }
          : {}),
      };

      setDeactivatedUser(null);
      if (user) {
        await updateUser(user.user_id, payload);
        toast.success("App user updated successfully.");
      } else {
        await createUser(payload);
        toast.success("App user created successfully.");
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      const apiData = err?.data;
      const apiMessage = apiData?.error || apiData?.message || err?.message;

      if (apiData?.is_deactivated && apiData?.user_id) {
        setDeactivatedUser({
          userId: apiData.user_id,
          name: apiData.full_name,
          email: apiData.email_id || form.email_id,
          field: apiData.field || "email_id",
          message: apiData.error || "Email exists for deactivated user.",
        });
        setError("");
      } else {
        setDeactivatedUser(null);
        setError(apiMessage || "Unable to save app user right now.");
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      {formTitle && (
        <div className={styles.headerRow}>
          <div>
            <h3 className={styles.headerTitle}>{formTitle}</h3>
            <p className={styles.headerHint}>
              {isIndustrial
                ? "Register industrial personnel (Directors, Plant Heads, Gate & Floor Receptionists, and Workforce)."
                : isCommercial
                ? "Register commercial occupants (Office Owners, Corporate Tenants, Business Managers, and Staff)."
                : "Register residential occupants (Flat Owners, Tenants, and Society Staff)."}
            </p>
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderRadius: "10px",
          marginBottom: "14px",
          background: isIndustrial
            ? "rgba(147, 51, 234, 0.08)"
            : isCommercial
            ? "rgba(37, 99, 235, 0.08)"
            : isHybrid
            ? "rgba(245, 158, 11, 0.08)"
            : "rgba(16, 185, 129, 0.08)",
          border: `1px solid ${
            isIndustrial
              ? "rgba(147, 51, 234, 0.25)"
              : isCommercial
              ? "rgba(37, 99, 235, 0.25)"
              : isHybrid
              ? "rgba(245, 158, 11, 0.25)"
              : "rgba(16, 185, 129, 0.25)"
          }`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "16px" }}>
            {isIndustrial ? "🏭" : isCommercial ? "🏢" : isHybrid ? "🏬" : "🏡"}
          </span>
          <div>
            <span
              style={{
                fontSize: "13px",
                fontWeight: "700",
                display: "block",
                color: isIndustrial ? "#7e22ce" : isCommercial ? "#1d4ed8" : isHybrid ? "#b45309" : "#047857",
              }}
            >
              {isIndustrial
                ? "Industrial Property Mode"
                : isCommercial
                ? "Commercial Complex Mode"
                : isHybrid
                ? "Hybrid Property Mode (Residential + Commercial)"
                : "Residential Society Mode"}
            </span>
            <span style={{ fontSize: "11px", color: "var(--color-muted)" }}>
              {isIndustrial
                ? "Form tailored for plant directors, receptionists, unit sheds, and shift logistics."
                : isCommercial
                ? "Form tailored for office units, corporate entities, business managers, and staff."
                : isHybrid
                ? "Supports both residential flats and commercial units. Form adapts dynamically to selected role."
                : "Form tailored for residential flats, family members, and society staff."}
            </span>
          </div>
        </div>
        <span
          style={{
            fontSize: "11px",
            fontWeight: "700",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            padding: "3px 8px",
            borderRadius: "6px",
            background: isIndustrial
              ? "rgba(147, 51, 234, 0.15)"
              : isCommercial
              ? "rgba(37, 99, 235, 0.15)"
              : isHybrid
              ? "rgba(245, 158, 11, 0.15)"
              : "rgba(16, 185, 129, 0.15)",
            color: isIndustrial ? "#7e22ce" : isCommercial ? "#1d4ed8" : isHybrid ? "#b45309" : "#047857",
          }}
        >
          {activePropertyType}
        </span>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {deactivatedUser && (
        <div className={styles.deactivatedBanner}>
          <div className={styles.deactivatedText}>
            <span className={styles.deactivatedTitle}>{deactivatedUser.message}</span>
            <span className={styles.deactivatedDesc}>
              User <strong>{deactivatedUser.name || deactivatedUser.email}</strong> is currently deactivated.
            </span>
          </div>
          <button
            type="button"
            className={styles.activateBtn}
            disabled={activating}
            onClick={handleActivateDeactivatedUser}
          >
            {activating ? "Activating..." : "Activate User?"}
          </button>
        </div>
      )}

      <div className={styles.tabs}>
        <button
          type="button"
          className={[styles.tab, activeTab === "personal" ? styles.tabActive : ""].filter(Boolean).join(" ")}
          onClick={() => setActiveTab("personal")}
        >
          Personal Info
        </button>
        {actualShowFamilyTab && (
          <button
            type="button"
            className={[styles.tab, activeTab === "family" ? styles.tabActive : ""].filter(Boolean).join(" ")}
            onClick={() => setActiveTab("family")}
          >
            {isCommercial ? "Authorized Personnel" : "Family Members"}
          </button>
        )}
        {actualShowVehiclesTab && (
          <button
            type="button"
            className={[styles.tab, activeTab === "vehicles" ? styles.tabActive : ""].filter(Boolean).join(" ")}
            onClick={() => setActiveTab("vehicles")}
          >
            {isIndustrial ? "Fleet & Vehicles" : "Vehicles"}
          </button>
        )}
        {isGuard && (
          <button
            type="button"
            className={[styles.tab, activeTab === "guardDetails" ? styles.tabActive : ""].filter(Boolean).join(" ")}
            onClick={() => setActiveTab("guardDetails")}
          >
            Guard Details
          </button>
        )}
      </div>

      {activeTab === "personal" && (
        <>
          <div className={styles.grid}>
            <label className={`${styles.label} ${styles.fullSpan}`}>
              <span className={styles.labelText}>
                Property / Society <span style={{ color: "#ef4444" }}>*</span>
              </span>
              <Select                name="society_id"
                value={form.society_id || ""}
                onChange={handleSocietyChange}
                disabled={readOnly || lockSociety}
                required
                className={fieldClassName}
              >
                {!lockSociety && (
                  <option value="">
                    {societies.length === 0 ? "Loading properties or none available..." : "Select Property / Society"}
                  </option>
                )}
                {societies.map((s) => (
                  <option key={s.society_id} value={String(s.society_id)}>
                    {s.society_name} {s.society_type ? `(${s.society_type})` : ""}
                  </option>
                ))}
              </Select>
            </label>

            <label className={styles.label}>
              <span className={styles.labelText}>
                Full Name <span style={{ color: "#ef4444" }}>*</span>
              </span>
              <input
                name="full_name"
                value={form.full_name}
                onChange={handleChange}
                disabled={readOnly}
                placeholder="Full Name"
                required
                className={fieldClassName}
              />
            </label>

            <label className={styles.label}>
              <span className={styles.labelText}>
                Email <span style={{ color: "#ef4444" }}>*</span>
              </span>
              <input
                type="email"
                name="email_id"
                value={form.email_id}
                onChange={handleChange}
                disabled={readOnly}
                placeholder="Email"
                required
                className={fieldClassName}
              />
            </label>

            <label className={styles.label}>
              <span className={styles.labelText}>
                Mobile Number <span style={{ color: "#ef4444" }}>*</span>
              </span>
              <input
                type="tel"
                name="mobile_number"
                value={form.mobile_number}
                onChange={handleChange}
                disabled={readOnly}
                placeholder="Mobile"
                inputMode="numeric"
                maxLength={10}
                required
                className={fieldClassName}
              />
            </label>

            <label className={styles.label}>
              <span className={styles.labelText}>
                Primary Role <span style={{ color: "#ef4444" }}>*</span>
              </span>
              <Select                name="primary_role"
                value={form.primary_role || ""}
                onChange={handleChange}
                disabled={readOnly}
                className={fieldClassName}
              >
                {primaryRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </Select>
            </label>

            <label className={styles.label}>
              <span className={styles.labelText}>Secondary Role</span>
              <Select                name="secondary_role"
                value={form.secondary_role || ""}
                onChange={handleChange}
                disabled={readOnly}
                className={fieldClassName}
              >
                <option value="">None</option>
                {secondaryRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </Select>
            </label>

            {showGenderField && (
              <label className={styles.label}>
                <span className={styles.labelText}>Gender</span>
                <Select                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  disabled={readOnly}
                  className={fieldClassName}
                >
                  <option>Male</option>
                  <option>Female</option>
                </Select>
              </label>
            )}

            {showOsTypeField && (
              <label className={styles.label}>
                <span className={styles.labelText}>Mobile OS</span>
                <Select                  name="os_type"
                  value={form.os_type}
                  onChange={handleChange}
                  disabled={readOnly}
                  className={fieldClassName}
                >
                  <option value="Android">Android</option>
                  <option value="iOS">iOS</option>
                </Select>
              </label>
            )}
          </div>

          {(isCommercial || isCommercialRole) && (
            <div className={styles.section} style={{ marginTop: "14px" }}>
              <div className={styles.sectionHeader}>
                <div>
                  <h4 className={styles.sectionTitle}>Commercial Entity Details</h4>
                  <p className={styles.headerHint} style={{ margin: "2px 0 0" }}>
                    Company identification, designation, and business contact information.
                  </p>
                </div>
              </div>

              {/* Business property quick-link */}
              {existingBusinesses.length > 0 && (
                <div className={styles.linkedEntityCard} style={{ marginBottom: "14px" }}>
                  <div className={styles.linkedEntityHeader}>
                    <span className={styles.linkedEntityTitle}>🏢 Link to Existing Business in Property</span>
                    {selectedBusinessName && (
                      <button
                        type="button"
                        className={styles.quickApplyBtn}
                        onClick={() => assignBusinessUnits(selectedBusinessName)}
                      >
                        ⚡ Assign All Units of This Business
                      </button>
                    )}
                  </div>
                  <p className={styles.headerHint} style={{ margin: "0 0 8px" }}>
                    Select an established business to inherit its profile and allocate its registered {unitTerm.toLowerCase()}s directly without needing an individual owner.
                  </p>
                  <Select                    value={selectedBusinessName}
                    onChange={(e) => handleSelectBusiness(e.target.value)}
                    disabled={readOnly}
                    className={fieldClassName}
                  >
                    <option value="">-- Choose Existing Business or enter new below --</option>
                    {existingBusinesses.map((b) => (
                      <option key={b.name} value={b.name}>
                        {b.name} ({b.units.length} registered {b.units.length === 1 ? unitTerm : `${unitTerm}s`})
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              <div className={styles.grid}>
                <label className={styles.label}>
                  <span className={styles.labelText}>Company / Business Name</span>
                  <input
                    name="role_details.company_name"
                    value={form.role_details?.company_name || ""}
                    onChange={handleChange}
                    disabled={readOnly}
                    placeholder="e.g. Asmita Tech Solutions Pvt Ltd"
                    className={fieldClassName}
                  />
                </label>

                <label className={styles.label}>
                  <span className={styles.labelText}>Job Title / Designation</span>
                  <input
                    name="role_details.designation"
                    value={form.role_details?.designation || ""}
                    onChange={handleChange}
                    disabled={readOnly}
                    placeholder="e.g. Managing Director, Branch Manager"
                    className={fieldClassName}
                  />
                </label>

                <label className={styles.label}>
                  <span className={styles.labelText}>GST / Business Reg. (Optional)</span>
                  <input
                    name="role_details.gst_number"
                    value={form.role_details?.gst_number || ""}
                    onChange={handleChange}
                    disabled={readOnly}
                    placeholder="e.g. 27AAAAA0000A1Z5"
                    className={fieldClassName}
                  />
                </label>

                <label className={styles.label}>
                  <span className={styles.labelText}>Office Extension / Phone (Optional)</span>
                  <input
                    name="role_details.office_phone"
                    value={form.role_details?.office_phone || ""}
                    onChange={handleChange}
                    disabled={readOnly}
                    placeholder="e.g. 022-28001234 Ext 104"
                    className={fieldClassName}
                  />
                </label>
              </div>
            </div>
          )}

          {(isIndustrial || isIndustrialRole) && (
            <div className={styles.section} style={{ marginTop: "14px" }}>
              <div className={styles.sectionHeader}>
                <div>
                  <h4 className={styles.sectionTitle}>Industrial Operations & Clearance Details</h4>
                  <p className={styles.headerHint} style={{ margin: "2px 0 0" }}>
                    Enterprise division, operational shift timing, and gate clearance level.
                  </p>
                </div>
              </div>

              {existingBusinesses.length > 0 && (
                <div className={styles.linkedEntityCard} style={{ marginBottom: "14px" }}>
                  <div className={styles.linkedEntityHeader}>
                    <span className={styles.linkedEntityTitle}>🏭 Link to Industrial Enterprise</span>
                    {selectedBusinessName && (
                      <button
                        type="button"
                        className={styles.quickApplyBtn}
                        onClick={() => assignBusinessUnits(selectedBusinessName)}
                      >
                        ⚡ Assign Enterprise Units
                      </button>
                    )}
                  </div>
                  <p className={styles.headerHint} style={{ margin: "0 0 8px" }}>
                    Select an industrial entity to allocate its registered sheds / production units directly.
                  </p>
                  <Select                    value={selectedBusinessName}
                    onChange={(e) => handleSelectBusiness(e.target.value)}
                    disabled={readOnly}
                    className={fieldClassName}
                  >
                    <option value="">-- Choose Enterprise / Company or enter custom below --</option>
                    {existingBusinesses.map((b) => (
                      <option key={b.name} value={b.name}>
                        {b.name} ({b.units.length} registered {b.units.length === 1 ? unitTerm : `${unitTerm}s`})
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              <div className={styles.grid}>
                <label className={styles.label}>
                  <span className={styles.labelText}>Department / Division</span>
                  <Select
                    name="role_details.department"
                    value={form.role_details?.department || (departmentOptions[0] || "Operations")}
                    onChange={handleChange}
                    disabled={readOnly}
                    className={fieldClassName}
                  >
                    {departmentOptions.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </Select>
                </label>

                <label className={styles.label}>
                  <span className={styles.labelText}>Employee ID</span>
                  <input
                    name="role_details.employee_id"
                    value={form.role_details?.employee_id || ""}
                    onChange={handleChange}
                    disabled={readOnly}
                    placeholder="e.g. EMP-10492"
                    className={fieldClassName}
                  />
                </label>

                <label className={styles.label}>
                  <span className={styles.labelText}>Job Title / Designation</span>
                  <input
                    name="role_details.designation"
                    value={form.role_details?.designation || ""}
                    onChange={handleChange}
                    disabled={readOnly}
                    placeholder="e.g. CFO, HOD, Lead Engineer, Plant Manager"
                    className={fieldClassName}
                  />
                </label>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                  <label className={styles.label}>
                    <span className={styles.labelText}>Desk / Workstation Code (Auto-Generated)</span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
                      <input
                        name="role_details.desk_code"
                        value={form.role_details?.desk_code || ""}
                        readOnly
                        placeholder="Auto-Generated"
                        className={fieldClassName}
                        style={{ background: '#f8fafc', fontFamily: 'monospace', fontWeight: 700, cursor: 'not-allowed', margin: 0 }}
                      />
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={handleRegenerateDeskCode}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            whiteSpace: 'nowrap',
                            height: '42px',
                            padding: '0 12px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '8px',
                            background: '#ffffff',
                            fontWeight: 600,
                            fontSize: '12px',
                            cursor: 'pointer',
                            color: '#334155'
                          }}
                          title="Regenerate Workstation Code"
                        >
                          <RefreshCw size={12} /> Regenerate
                        </button>
                      )}
                    </div>
                  </label>

                  <label className={styles.label}>
                    <span className={styles.labelText}>Cabin Code (Optional)</span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
                      <input
                        name="role_details.cabin_code"
                        value={form.role_details?.cabin_code || ""}
                        readOnly
                        placeholder="No Cabin Assigned"
                        className={fieldClassName}
                        style={{ background: '#f8fafc', fontFamily: 'monospace', fontWeight: 700, cursor: 'not-allowed', margin: 0 }}
                      />
                      {!readOnly && (
                        form.role_details?.cabin_code ? (
                          <>
                            <button
                              type="button"
                              onClick={handleRegenerateCabinCode}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                whiteSpace: 'nowrap',
                                height: '42px',
                                padding: '0 12px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                background: '#ffffff',
                                fontWeight: 600,
                                fontSize: '12px',
                                cursor: 'pointer',
                                color: '#334155'
                              }}
                              title="Regenerate Cabin Code"
                            >
                              <RefreshCw size={12} /> Regenerate
                            </button>
                            <button
                              type="button"
                              onClick={handleClearCabinCode}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: '42px',
                                padding: '0 10px',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                background: '#fff1f2',
                                color: '#e11d48',
                                cursor: 'pointer'
                              }}
                              title="Clear Cabin"
                            >
                              <X size={13} />
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={handleRegenerateCabinCode}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              whiteSpace: 'nowrap',
                              height: '42px',
                              padding: '0 12px',
                              border: '1px solid #cbd5e1',
                              borderRadius: '8px',
                              background: '#ffffff',
                              fontWeight: 600,
                              fontSize: '12px',
                              cursor: 'pointer',
                              color: '#334155'
                            }}
                            title="Assign Auto-Generated Cabin"
                          >
                            <Plus size={12} /> Assign Cabin
                          </button>
                        )
                      )}
                    </div>
                  </label>
                </div>

                <label className={styles.label}>
                  <span className={styles.labelText}>Reporting Head / Manager / TL</span>
                  <Select
                    name="role_details.reports_to_id"
                    value={form.role_details?.reports_to_id || ""}
                    onChange={handleChange}
                    disabled={readOnly}
                    className={fieldClassName}
                  >
                    <option value="">-- No Direct Reporting Manager (Top Level / Self) --</option>
                    {societyUsers
                      .filter((u) => !user?.user_id || Number(u.user_id) !== Number(user.user_id))
                      .map((u) => (
                        <option key={u.user_id} value={u.user_id}>
                          {u.full_name} ({u.primary_role || "User"}{u.role_details?.department ? ` - ${u.role_details.department}` : ""})
                        </option>
                      ))}
                  </Select>
                </label>

                <label className={styles.label}>
                  <span className={styles.labelText}>Operational Shift</span>
                  <Select
                    name="role_details.shift"
                    value={form.role_details?.shift || "General"}
                    onChange={handleChange}
                    disabled={readOnly}
                    className={fieldClassName}
                  >
                    <option value="General">General Shift (9:00 AM - 6:00 PM)</option>
                    <option value="Morning">Morning Shift (6:00 AM - 2:00 PM)</option>
                    <option value="Evening">Evening Shift (2:00 PM - 10:00 PM)</option>
                    <option value="Night">Night Shift (10:00 PM - 6:00 AM)</option>
                    <option value="Rotating">Rotating 24/7 Shift</option>
                  </Select>
                </label>

                <label className={styles.label}>
                  <span className={styles.labelText}>Gate Access Clearance</span>
                  <Select
                    name="role_details.gate_clearance"
                    value={form.role_details?.gate_clearance || "All Gates"}
                    onChange={handleChange}
                    disabled={readOnly}
                    className={fieldClassName}
                  >
                    <option value="All Gates">All Gates (Executive Clearance)</option>
                    <option value="Main Gate Only">Main Gate Only</option>
                    <option value="Loading Bay Gate">Cargo & Loading Bay Gate</option>
                    <option value="Staff Gate">Pedestrian Staff Gate</option>
                  </Select>
                </label>
              </div>

              {/* Delegation Authority Settings: Is Delegator & Is Delegatee */}
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "12px 14px", marginTop: "14px" }}>
                <span style={{ fontSize: "12.5px", fontWeight: "700", color: "#334155", display: "block", marginBottom: "8px" }}>
                  Visitor Approval Delegation Capabilities
                </span>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: readOnly ? "default" : "pointer", userSelect: "none" }}>
                    <input
                      type="checkbox"
                      name="role_details.is_delegator"
                      checked={form.role_details?.is_delegator !== false}
                      onChange={(e) => {
                        setForm((prev) => ({
                          ...prev,
                          role_details: {
                            ...(prev.role_details || {}),
                            is_delegator: e.target.checked,
                          },
                        }));
                      }}
                      disabled={readOnly}
                      style={{ marginTop: "2px", accentColor: "var(--color-dark-bg, #27306f)", cursor: readOnly ? "default" : "pointer", width: "16px", height: "16px" }}
                    />
                    <span style={{ display: "flex", flexDirection: "column" }}>
                      <strong style={{ fontSize: "12.5px", color: "#1e293b" }}>Is Delegator</strong>
                      <span style={{ fontSize: "11px", color: "#64748b", lineHeight: 1.3 }}>
                        Can delegate own visitor approvals to eligible colleagues when away or busy.
                      </span>
                    </span>
                  </label>

                  <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: readOnly ? "default" : "pointer", userSelect: "none" }}>
                    <input
                      type="checkbox"
                      name="role_details.is_delegatee"
                      checked={form.role_details?.is_delegatee !== false}
                      onChange={(e) => {
                        setForm((prev) => ({
                          ...prev,
                          role_details: {
                            ...(prev.role_details || {}),
                            is_delegatee: e.target.checked,
                          },
                        }));
                      }}
                      disabled={readOnly}
                      style={{ marginTop: "2px", accentColor: "var(--color-dark-bg, #27306f)", cursor: readOnly ? "default" : "pointer", width: "16px", height: "16px" }}
                    />
                    <span style={{ display: "flex", flexDirection: "column" }}>
                      <strong style={{ fontSize: "12.5px", color: "#1e293b" }}>Is Delegatee</strong>
                      <span style={{ fontSize: "11px", color: "#64748b", lineHeight: 1.3 }}>
                        Appears in colleagues&apos; delegate list to receive and act on delegated visitor entries.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {isTenant && (
            <div className={styles.section} style={{ marginTop: "14px" }}>
              <div className={styles.sectionHeader}>
                <div>
                  <h4 className={styles.sectionTitle}>Tenant Property & Occupancy Linkage</h4>
                  <p className={styles.headerHint} style={{ margin: "2px 0 0" }}>
                    Link this tenant directly to a property owner or a corporate business entity.
                  </p>
                </div>
              </div>

              <div className={styles.linkToggleGroup}>
                <button
                  type="button"
                  className={`${styles.linkToggleBtn} ${linkType === "none" ? styles.linkToggleBtnActive : ""}`}
                  onClick={() => {
                    setLinkType("none");
                    setSelectedOwnerId("");
                    setSelectedBusinessName("");
                  }}
                  disabled={readOnly}
                >
                  📄 Direct Lease
                </button>
                <button
                  type="button"
                  className={`${styles.linkToggleBtn} ${linkType === "owner" ? styles.linkToggleBtnActive : ""}`}
                  onClick={() => setLinkType("owner")}
                  disabled={readOnly}
                >
                  👤 Link to Owner
                </button>
                <button
                  type="button"
                  className={`${styles.linkToggleBtn} ${linkType === "business" ? styles.linkToggleBtnActive : ""}`}
                  onClick={() => setLinkType("business")}
                  disabled={readOnly}
                >
                  🏢 Link to Business / Company
                </button>
              </div>

              {linkType === "owner" && (
                <div className={styles.linkedEntityCard}>
                  <div className={styles.linkedEntityHeader}>
                    <span className={styles.linkedEntityTitle}>Select Registered Property Owner</span>
                  </div>
                  <p className={styles.headerHint} style={{ margin: "0 0 8px" }}>
                    Selecting an owner automatically links and pre-fills their owned {unitTerm.toLowerCase()}s for this tenant lease.
                  </p>
                  <Select                    value={selectedOwnerId}
                    onChange={(e) => handleSelectOwner(e.target.value)}
                    disabled={readOnly}
                    className={fieldClassName}
                  >
                    <option value="">-- Choose Property Owner --</option>
                    {existingOwners.map((owner) => (
                      <option key={owner.id} value={String(owner.id)}>
                        {owner.name} ({owner.email || owner.mobile || "Owner"}) {owner.units?.length ? `• ${owner.units.length} unit(s)` : ""}
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              {linkType === "business" && (
                <div className={styles.linkedEntityCard}>
                  <div className={styles.linkedEntityHeader}>
                    <span className={styles.linkedEntityTitle}>Select Registered Business / Company</span>
                    {selectedBusinessName && (
                      <button
                        type="button"
                        className={styles.quickApplyBtn}
                        onClick={() => assignBusinessUnits(selectedBusinessName)}
                      >
                        ⚡ Apply Business Units
                      </button>
                    )}
                  </div>
                  <p className={styles.headerHint} style={{ margin: "0 0 8px" }}>
                    Link to a registered company or enter business details below. You can assign the business&apos;s properties directly.
                  </p>
                  {existingBusinesses.length > 0 ? (
                    <Select                      value={selectedBusinessName}
                      onChange={(e) => handleSelectBusiness(e.target.value)}
                      disabled={readOnly}
                      className={fieldClassName}
                    >
                      <option value="">-- Choose Business Entity --</option>
                      {existingBusinesses.map((b) => (
                        <option key={b.name} value={b.name}>
                          {b.name} ({b.units.length} registered {b.units.length === 1 ? unitTerm : `${unitTerm}s`})
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <p className={styles.headerHint}>No existing businesses registered in this property yet. Enter company name in details below.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {towerLoadError ? <p className={styles.error}>{towerLoadError}</p> : null}

          {isFloorReceptionist && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <div>
                  <h4 className={styles.sectionTitle}>Floor Receptionist Desk Assignment</h4>
                  <p className={styles.headerHint} style={{ margin: "2px 0 0" }}>
                    Select which floor this receptionist manages. Visitors arriving for directors on this floor will be routed here for approval.
                  </p>
                </div>
              </div>

              <div className={styles.grid}>
                <label className={styles.label}>
                  <span className={styles.labelText}>
                    Assigned Tower / Building <span style={{ color: "#ef4444" }}>*</span>
                  </span>
                  <Select                    value={receptionistTowerId}
                    onChange={(e) => {
                      setReceptionistTowerId(e.target.value);
                      setReceptionistFloor("");
                      setReceptionistDeskFlatId("");
                    }}
                    disabled={readOnly || !form.society_id}
                    className={fieldClassName}
                  >
                    <option value="">Select Tower</option>
                    {towers.map((t) => (
                      <option key={t.tower_id} value={String(t.tower_id)}>
                        {t.tower_name}
                      </option>
                    ))}
                  </Select>
                </label>

                <label className={styles.label}>
                  <span className={styles.labelText}>
                    Assigned Floor <span style={{ color: "#ef4444" }}>*</span>
                  </span>
                  <Select                    value={receptionistFloor}
                    onChange={(e) => {
                      setReceptionistFloor(e.target.value);
                      setReceptionistDeskFlatId("");
                    }}
                    disabled={readOnly || !receptionistTowerId}
                    className={fieldClassName}
                  >
                    <option value="">Select Floor</option>
                    {availableFloorsForTower.map((fl) => (
                      <option key={fl} value={String(fl)}>
                        Floor {fl}
                      </option>
                    ))}
                  </Select>
                </label>

                <label className={styles.label}>
                  <span className={styles.labelText}>Reception Desk Unit (Optional)</span>
                  <Select                    value={receptionistDeskFlatId}
                    onChange={(e) => setReceptionistDeskFlatId(e.target.value)}
                    disabled={readOnly || !receptionistFloor}
                    className={fieldClassName}
                  >
                    <option value="">Select Desk Unit (Optional)</option>
                    {availableDeskFlats.map((f) => (
                      <option key={f.flat_id} value={String(f.flat_id)}>
                        {f.flat_number} {f.unit_type ? `(${f.unit_type})` : ""}
                      </option>
                    ))}
                  </Select>
                </label>
              </div>
            </div>
          )}

          {showFlatAssignments && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4 className={styles.sectionTitle}>{unitTerm} Assignments</h4>
                {!readOnly && (
                  <button type="button" className={styles.linkButton} onClick={addFlatRow}>
                    + Add {unitTerm}
                  </button>
                )}
              </div>

              <div className={styles.flatRows}>
                {flatMappings.map((row, index) => {
                  const towerFlats = row.tower_id ? flatOptionsByTower.get(String(row.tower_id)) || [] : [];
                  return (
                    <div key={index} className={styles.flatRow}>
                      <label className={styles.label}>
                        <span className={styles.labelText}>
                          {isIndustrial ? "Plant / Building" : isCommercial ? "Wing / Block" : "Tower"}
                        </span>
                        <Select                          value={row.tower_id}
                          onChange={(e) => handleFlatMappingChange(index, "tower_id", e.target.value)}
                          disabled={readOnly || !form.society_id}
                          className={fieldClassName}
                        >
                          <option value="">
                            Select {isIndustrial ? "Plant / Building" : isCommercial ? "Wing / Block" : "Tower"}
                          </option>
                          {towers.map((t) => (
                            <option key={t.tower_id} value={String(t.tower_id)}>
                              {t.tower_name}
                            </option>
                          ))}
                        </Select>
                      </label>

                      <label className={styles.label}>
                        <span className={styles.labelText}>{unitTerm}</span>
                        <Select                          value={row.flat_id}
                          onChange={(e) => handleFlatMappingChange(index, "flat_id", e.target.value)}
                          disabled={readOnly || !row.tower_id}
                          className={fieldClassName}
                        >
                          <option value="">Select {unitTerm}</option>
                          {towerFlats.map((f) => (
                            <option key={f.flat_id} value={String(f.flat_id)}>
                              {f.flat_number}
                              {f.floors?.floor_number != null
                                ? ` • Floor ${f.floors.floor_number}`
                                : ""}
                              {f.unit_type ? ` (${f.unit_type})` : ""}
                            </option>
                          ))}
                        </Select>
                      </label>

                      <label className={styles.label}>
                        <span className={styles.labelText}>Occupancy Type</span>
                        <Select                          value={row.ownership_type}
                          onChange={(e) => handleFlatMappingChange(index, "ownership_type", e.target.value)}
                          disabled={readOnly}
                          className={fieldClassName}
                        >
                          {occupancyOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Select>
                      </label>

                      {!readOnly && flatMappings.length > 1 && (
                        <div className={styles.removeWrap}>
                          <button
                            type="button"
                            className={styles.removeButton}
                            onClick={() => removeFlatRow(index)}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {showTowerAssignments && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4 className={styles.sectionTitle}>Tower Assignment</h4>
                {!readOnly && (
                  <button type="button" className={styles.linkButton} onClick={addTowerRow}>
                    + Add Tower
                  </button>
                )}
              </div>
              <div className={styles.flatRows}>
                {towerMappings.map((row, index) => (
                  <div key={index} className={styles.flatRow}>
                    <label className={styles.label}>
                      <span className={styles.labelText}>Tower</span>
                      <Select                        value={row.tower_id}
                        onChange={(e) => handleTowerMappingChange(index, "tower_id", e.target.value)}
                        disabled={readOnly || !form.society_id}
                        className={fieldClassName}
                      >
                        <option value="">Select Tower</option>
                        {towers.map((t) => (
                          <option key={t.tower_id} value={String(t.tower_id)}>
                            {t.tower_name}
                          </option>
                        ))}
                      </Select>
                    </label>

                    {!readOnly && towerMappings.length > 1 && (
                      <div className={styles.removeWrap}>
                        <button
                          type="button"
                          className={styles.removeButton}
                          onClick={() => removeTowerRow(index)}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === "family" && actualShowFamilyTab && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div>
              <h4 className={styles.sectionTitle}>
                {isCommercial ? "Authorized Personnel & Office Keyholders" : "Family Members"}
              </h4>
              <p className={styles.headerHint} style={{ margin: "2px 0 0" }}>
                {isCommercial
                  ? "Add authorized keyholders, partners, or emergency office contacts associated with this unit."
                  : "Add immediate family members residing in the same unit."}
              </p>
            </div>
            {!readOnly && (
              <button type="button" className={styles.linkButton} onClick={addFamilyMember}>
                + Add {isCommercial ? "Personnel" : "Member"}
              </button>
            )}
          </div>
          <div className={styles.flatRows}>
            {familyMembers.map((member, index) => (
              <div key={index} className={styles.flatRow}>
                <label className={styles.label}>
                  <span className={styles.labelText}>Name</span>
                  <input
                    value={member.member_name}
                    onChange={(e) => updateFamilyMember(index, "member_name", e.target.value)}
                    disabled={readOnly}
                    placeholder={isCommercial ? "Full Name / Staff Name" : "Member Name"}
                    className={fieldClassName}
                  />
                </label>
                <label className={styles.label}>
                  <span className={styles.labelText}>
                    {isCommercial ? "Role / Title" : "Relation"}
                  </span>
                  <input
                    value={member.relation}
                    onChange={(e) => updateFamilyMember(index, "relation", e.target.value)}
                    disabled={readOnly}
                    placeholder={isCommercial ? "e.g. Partner, Manager, Accountant" : "e.g. Spouse, Child"}
                    className={fieldClassName}
                  />
                </label>
                <label className={styles.label}>
                  <span className={styles.labelText}>Type</span>
                  <Select                    value={member.member_type}
                    onChange={(e) => updateFamilyMember(index, "member_type", e.target.value)}
                    disabled={readOnly}
                    className={fieldClassName}
                  >
                    {isCommercial ? (
                      <>
                        <option value="Adult">Executive / Partner</option>
                        <option value="Senior">Senior Staff / Manager</option>
                        <option value="Other">Keyholder / Associate</option>
                      </>
                    ) : (
                      <>
                        <option value="Adult">Adult</option>
                        <option value="Child">Child</option>
                        <option value="Senior">Senior</option>
                        <option value="Other">Other</option>
                      </>
                    )}
                  </Select>
                </label>
                {!readOnly && familyMembers.length > 1 && (
                  <div className={styles.removeWrap}>
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => removeFamilyMember(index)}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "vehicles" && actualShowVehiclesTab && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div>
              <h4 className={styles.sectionTitle}>
                {isIndustrial ? "Fleet & Registered Vehicles" : "Registered Vehicles"}
              </h4>
              <p className={styles.headerHint} style={{ margin: "2px 0 0" }}>
                {isIndustrial
                  ? "Register transport trucks, delivery vans, forklifts, and employee vehicles."
                  : isCommercial
                  ? "Register office pool cars, executive vehicles, and delivery transport."
                  : "Register resident vehicles for society gate and parking access."}
              </p>
            </div>
            {!readOnly && (
              <button type="button" className={styles.linkButton} onClick={addVehicle}>
                + Add Vehicle
              </button>
            )}
          </div>
          <div className={styles.flatRows}>
            {vehicles.map((v, index) => (
              <div key={index} className={styles.flatRow}>
                <label className={styles.label}>
                  <span className={styles.labelText}>Plate Number</span>
                  <input
                    value={v.vehicle_number}
                    onChange={(e) => updateVehicle(index, "vehicle_number", e.target.value)}
                    disabled={readOnly}
                    placeholder="MH-04-AB-1234"
                    className={fieldClassName}
                  />
                </label>
                <label className={styles.label}>
                  <span className={styles.labelText}>Type</span>
                  <Select                    value={v.vehicle_type}
                    onChange={(e) => updateVehicle(index, "vehicle_type", e.target.value)}
                    disabled={readOnly}
                    className={fieldClassName}
                  >
                    {isIndustrial ? (
                      <>
                        <option value="Commercial Truck">Commercial Truck / Lorry</option>
                        <option value="Delivery Van">Pickup / Delivery Van</option>
                        <option value="Forklift">Forklift / Industrial Machine</option>
                        <option value="Car">Car</option>
                        <option value="Two Wheeler">Two Wheeler</option>
                        <option value="EV">EV</option>
                        <option value="Other">Other</option>
                      </>
                    ) : (
                      <>
                        <option value="Car">Car</option>
                        <option value="Two Wheeler">Two Wheeler</option>
                        <option value="EV">EV</option>
                        <option value="Commercial Van">Commercial Van</option>
                        <option value="Other">Other</option>
                      </>
                    )}
                  </Select>
                </label>
                <label className={styles.label}>
                  <span className={styles.labelText}>Model & Color</span>
                  <input
                    value={v.model}
                    onChange={(e) => updateVehicle(index, "model", e.target.value)}
                    disabled={readOnly}
                    placeholder="e.g. Honda City White"
                    className={fieldClassName}
                  />
                </label>
                {!readOnly && vehicles.length > 1 && (
                  <div className={styles.removeWrap}>
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => removeVehicle(index)}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "guardDetails" && isGuard && (
        <div className={styles.grid}>
          <label className={styles.label}>
            <span className={styles.labelText}>Shift Timing</span>
            <Select              name="role_details.shift"
              className={fieldClassName}
              value={form.role_details?.shift || "Day"}
              onChange={handleChange}
              disabled={readOnly}
            >
              <option value="Day">Day Shift (08:00 - 20:00)</option>
              <option value="Night">Night Shift (20:00 - 08:00)</option>
              <option value="Rotational">Rotational</option>
            </Select>
          </label>
          <label className={styles.label}>
            <span className={styles.labelText}>Agency Name</span>
            <input
              name="role_details.agency"
              className={fieldClassName}
              placeholder="Security Agency Pvt Ltd"
              value={form.role_details?.agency || ""}
              onChange={handleChange}
              disabled={readOnly}
            />
          </label>
        </div>
      )}

      {!readOnly && (
        <button type="submit" className={styles.submit}>
          {user ? "Update App User" : "Create App User"}
        </button>
      )}
    </form>
  );
};

export default AppUserForm;
